import React, { useId, useState } from 'react';
import { Box, IconButton, InputAdornment, TextField, TextFieldProps } from '@mui/material';
import { VisibilityOutlined as ShowIcon, VisibilityOffOutlined as HideIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

type AuthTextFieldProps = Omit<TextFieldProps, 'label' | 'variant'> & {
  label: string;
  // Rendered at the right end of the label row, e.g. a "Forgot password?" link.
  labelAction?: React.ReactNode;
};

// Static label above a 40px input, as in the auth mockups. Password fields get a show/hide toggle.
export const AuthTextField = ({ label, labelAction, type, id, sx, ...rest }: AuthTextFieldProps) => {
  const { t } = useTranslation('auth');
  const autoId = useId();
  const inputId = id ?? autoId;
  const [reveal, setReveal] = useState(false);
  const isPassword = type === 'password';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
        <Box component="label" htmlFor={inputId} sx={{ fontSize: 14, fontWeight: 500 }}>
          {label}
        </Box>
        {labelAction}
      </Box>
      <TextField
        {...rest}
        id={inputId}
        type={isPassword && reveal ? 'text' : type}
        fullWidth
        hiddenLabel
        size="small"
        sx={[{ '& .MuiOutlinedInput-root': { minHeight: 40, fontSize: 14 } }, ...(Array.isArray(sx) ? sx : [sx])]}
        slotProps={
          isPassword
            ? {
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        onClick={() => setReveal(r => !r)}
                        aria-label={reveal ? t('field.hidePassword') : t('field.showPassword')}
                        sx={{ color: 'var(--muted)' }}
                      >
                        {reveal ? <HideIcon sx={{ fontSize: 18 }} /> : <ShowIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }
            : undefined
        }
      />
    </Box>
  );
};
